import React, { useEffect, useState } from 'react';
import { getProfile, updateProfile } from '../api';
import SettingsNav from '../components/SettingsNav';
import './Settings.css';

const GOAL_SUGGESTIONS = [
  'Cash flow optimization', 'Investment evaluation', 'Break-even planning',
  'Fundraising readiness', 'Cost reduction', 'Revenue growth',
];

const TIER_META = {
  starter: { label: 'Starter', color: '#6B7280' },
  pro:     { label: 'Pro',     color: '#D4A017' },
  vip:     { label: 'VIP',     color: '#F0C040' },
};

const Field = ({ label, value, onChange, type = 'text', mono, readOnly }) => (
  <label className="settings-field">
    <span className="settings-field__label">{label}</span>
    <input
      type={type}
      className={`settings-field__input${mono ? ' settings-field__input--mono' : ''}`}
      value={value ?? ''}
      onChange={onChange}
      readOnly={readOnly}
    />
  </label>
);

/**
 * SettingsInfo — /settings/info
 *
 * Real account data (not the old placeholder), fetched from
 * GET /api/v1/users/profile, editable and saved via
 * PATCH /api/v1/users/profile.
 */
const SettingsInfo = () => {
  const [user, setUser] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await getProfile();
        setUser(res.data.user);
        setForm(res.data.user);
      } catch (err) {
        setError(err.message || 'Could not load your profile.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (field) => (e) => {
    setSaved(false);
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const toggleGoal = (goal) => {
    setSaved(false);
    setForm((f) => {
      const goals = f.financialGoals || [];
      return {
        ...f,
        financialGoals: goals.includes(goal) ? goals.filter((g) => g !== goal) : [...goals, goal],
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await updateProfile({
        firstName: form.firstName,
        lastName: form.lastName,
        organization: form.organization,
        profession: form.profession,
        phoneNumber: form.phoneNumber,
        addressLine1: form.addressLine1,
        addressLine2: form.addressLine2,
        unitBldgApt: form.unitBldgApt,
        city: form.city,
        stateProvince: form.stateProvince,
        zipCode: form.zipCode,
        country: form.country,
        annualIncome: form.annualIncome === '' ? null : Number(form.annualIncome),
        financialGoals: form.financialGoals,
      });
      setUser(res.data.user);
      setForm(res.data.user);
      setSaved(true);
    } catch (err) {
      setError(err.message || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main className="settings-page">
        <SettingsNav />
        <p className="settings-note">Loading your profile…</p>
      </main>
    );
  }

  if (!form) {
    return (
      <main className="settings-page">
        <SettingsNav />
        <p className="settings-error">{error || 'Could not load your profile.'}</p>
      </main>
    );
  }

  const meta = TIER_META[user.tier] || TIER_META.starter;
  const initials = `${(form.firstName || '?')[0]}${(form.lastName || '')[0] || ''}`;

  return (
    <main className="settings-page">
      <SettingsNav />

      <div className="settings-page__header">
        <div>
          <h1 className="settings-page__title">My Info</h1>
          <p className="settings-page__sub">Your account details.</p>
        </div>
        <button className="btn btn--md btn--gold" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      <div className="settings-identity glass-card">
        <div className="settings-identity__avatar" style={{ '--tier-color': meta.color }}>
          {initials.toUpperCase()}
        </div>
        <div className="settings-identity__info">
          <h2 className="settings-identity__name">{form.firstName} {form.lastName}</h2>
          <p className="settings-identity__email">{user.email}</p>
        </div>
        <div className="settings-identity__meta">
          <span className="settings-tier-badge" style={{ '--tier-color': meta.color }}>{meta.label}</span>
          <span className="settings-identity__since">
            Member since {new Date(user.createdAt).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
          </span>
        </div>
      </div>

      {error && <p className="settings-error">{error}</p>}
      {saved && <p className="settings-saved">Saved.</p>}

      <div className="settings-grid">
        <div className="settings-col">
          <section className="settings-section glass-card">
            <h3 className="settings-section__title">Personal Information</h3>
            <Field label="First name" value={form.firstName} onChange={set('firstName')} />
            <Field label="Last name" value={form.lastName} onChange={set('lastName')} />
            <Field label="Email address" value={user.email} onChange={() => {}} type="email" readOnly />
            <p className="settings-field__hint">Email can't be changed here.</p>
            <Field label="Phone" value={form.phoneNumber} onChange={set('phoneNumber')} />
          </section>

          <section className="settings-section glass-card">
            <h3 className="settings-section__title">Professional</h3>
            <Field label="Profession / Career" value={form.profession} onChange={set('profession')} />
            <Field label="Organization" value={form.organization} onChange={set('organization')} />
          </section>
        </div>

        <div className="settings-col">
          <section className="settings-section glass-card">
            <h3 className="settings-section__title">Address</h3>
            <Field label="Address line 1" value={form.addressLine1} onChange={set('addressLine1')} />
            <Field label="Address line 2" value={form.addressLine2} onChange={set('addressLine2')} />
            <Field label="Unit / Bldg / Apt" value={form.unitBldgApt} onChange={set('unitBldgApt')} />
            <Field label="City" value={form.city} onChange={set('city')} />
            <Field label="State / Province" value={form.stateProvince} onChange={set('stateProvince')} />
            <Field label="ZIP" value={form.zipCode} onChange={set('zipCode')} mono />
            <Field label="Country" value={form.country} onChange={set('country')} />
          </section>

          <section className="settings-section glass-card">
            <h3 className="settings-section__title">Financial Profile</h3>
            <Field
              label="Annual income (USD)"
              type="number"
              value={form.annualIncome}
              onChange={set('annualIncome')}
              mono
            />
            <div className="settings-field">
              <span className="settings-field__label">Financial goals</span>
              <div className="settings-tags">
                {GOAL_SUGGESTIONS.map((g) => (
                  <button
                    key={g}
                    type="button"
                    className={`settings-tag${(form.financialGoals || []).includes(g) ? ' settings-tag--selected' : ''}`}
                    onClick={() => toggleGoal(g)}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};

export default SettingsInfo;
